import { useContext } from "react";
import { log } from "./utils.js";
import { GameContext } from "./App.js";

function Square({ value, onSquareClick, isHighlighted = false }) {
    return (
        <button className={"square" + (isHighlighted ? " hl-winner" : "")} onClick={onSquareClick}>
            {value}
        </button>
    );
}

/**
 * Board component for rendering the Tic-Tac-Toe game board.
 * 
 * @param {Object} props - The component props.
 * @param {string[]} props.squares - An array of strings representing the state of the 9 squares.
 * @param {boolean} props.xIsNext - A boolean indicating whether the next player is "X".
 * @param {function(number): void} props.onPlayerMove
 */
export function Board({ squares, xIsNext, onPlayerMove }) {
    const { gameStarted, thisPlayerIsX } = useContext(GameContext);

    /**
     * Callback function for when a square is clicked.
     * @param {number} index - The index of the clicked square.
     */
    function handleClick(index) {
        if (!gameStarted) {
            log("[Game] The game has yet to start.")
            return;
        }
        if (checkWinner(squares)) {
            log("[Game] Game finished already!");
            return;
        }
        if (squares[index]) {
            log("[Game] Square '" + index + "' already occupied!");
            return;
        }

        const thisPlayerIsNext = thisPlayerIsX === xIsNext; // TODO this is duplicated in App
        if (!thisPlayerIsNext) {
            log("[Game] It's the other player's turn!");
            return;
        }

        onPlayerMove(index);
    }

    const winIndexes = checkWinner(squares);
    let gameStatus;
    if (winIndexes) {
        gameStatus = "WINNER: " + squares[winIndexes[0]];
    } else if (squares.every(sq => sq)) {
        // All occupied
        gameStatus = "Draw!";
    } else {
        gameStatus = "Next Player: " + (xIsNext ? "X" : "O");
        gameStatus = xIsNext === thisPlayerIsX ?
            "Your turn!" : "Waiting for opponent...";
    }

    const sqRows = [];
    for (let row = 0; row < 3; row++) {
        const sqRow = [];
        for (let col = 0; col < 3; col++) {
            const sqIndex = row * 3 + col;
            sqRow[col] = (
                <Square key={sqIndex}
                    value={squares[sqIndex]}
                    onSquareClick={() => handleClick(sqIndex)}
                    isHighlighted={winIndexes?.includes(sqIndex)}
                />
            );
        }
        sqRows.push(<div key={row} className="board-row">{sqRow}</div>);
    }
    return (
        <>
            <div className="status"><h5>{gameStatus}</h5></div>
            {sqRows}
        </>
    );
}


/**
 * Checks whether the given gamestate satisfies a win condition and if so returns
 * the winner. 
 * @param {string[]} squares The state consisting of the 9 square values.
 * @returns Returns an array containing the square indices representing the winning
 * pattern. If no winner exists null is returned.
 */
function checkWinner(squares) {
    const winConditions = [
        [0, 1, 2],
        [3, 4, 5],
        [6, 7, 8],
        [0, 3, 6],
        [1, 4, 7],
        [2, 5, 8],
        [0, 4, 8],
        [2, 4, 6]];

    for (const [ia, ib, ic] of winConditions) {
        if (squares[ia] && squares[ia] === squares[ib] && squares[ia] === squares[ic]) {
            return [ia, ib, ic];
        }
    }
    return null;
}